import {
  FormControl,
  FormErrorMessage,
  VStack,
  Button,
  Icon,
  Flex,
  Tooltip,
  Text,
  Center,
} from '@wegroup/design-system';
import React, { useLayoutEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useAnswerForQuestion from '../../../../../hooks/useAnswerForQuestion';
import useFlowInputComponent from '../../../../../hooks/useFlowsInputComponent';
import { FlowsQuestion } from '../../../../../types/FlowsTypes';
import useNaceCodeOptions from '../hooks/useNaceCodeOptions';
import useTargetQuestion from '../hooks/useTargetQuestion';
import SingleIndexPercentageInput, {
  KVPercentage,
} from './SingleIndexPercentageInput';

interface Props {
  question: FlowsQuestion;
}

const IndexPercentageInputComponent: React.FC<
  React.PropsWithChildren<Props>
> = ({ question }) => {
  const { t } = useTranslation();
  const {
    field: { value, onBlur },
    error,
  } = useFlowInputComponent({ question });
  const getAnswerForQuestion = useAnswerForQuestion();
  const enumOptions = useNaceCodeOptions(question);
  const chosenValues = (value || []) as KVPercentage[];
  const availableOptions = enumOptions.filter(
    ({ value }) => !chosenValues.map(({ key }) => key).includes(value),
  );

  const targetQuestion = useTargetQuestion(question);
  const targetQuestionAnswer = getAnswerForQuestion(targetQuestion!.id);
  // TODO: Make useFlowsAnswers hook
  const { setValue } = useFormContext();

  /**
   * We want to make sure that the answer is cleared if it is no longer an answer for the targetQuestion
   */
  useLayoutEffect(() => {
    const newValues = chosenValues.filter(({ key }) =>
      enumOptions.map(({ value }) => value).includes(key),
    );

    setValue(question.id, newValues);
    onBlur({ name: question.id, value: newValues });
  }, [targetQuestionAnswer]);

  const handleAddNewEntry = () => {
    // Check first key that does not occur yet
    // Add it to the list with default percentage 0
    const firstAvailableOption = availableOptions[0];
    if (firstAvailableOption) {
      onBlur({
        name: question.id,
        value: [
          ...chosenValues,
          {
            key: firstAvailableOption.value,
            value: 0,
          },
        ],
      });
    }
  };

  const totalPercentage = chosenValues.reduce(
    (acc, { value }) => acc + value,
    0,
  );

  return (
    <FormControl isInvalid={!!error}>
      <VStack spacing="4" align="stretch">
        <VStack spacing="3" align="stretch">
          {chosenValues.length ? (
            chosenValues.map(({ key, value }) => (
              <SingleIndexPercentageInput
                key={key}
                question={question}
                keyName={key}
                value={value}
              />
            ))
          ) : (
            <Center
              p="6"
              bg="gray.ultraLight"
              border="1px"
              borderColor="gray.50"
              borderRadius="md"
              boxShadow="level1"
            >
              <Text color="gray.500">
                {t('COMPANY-SCAN_INPUT_INDEX-PERCENTAGE_EMPTY')}
              </Text>
            </Center>
          )}
          {totalPercentage > 1 && (
            <Text color="danger.main" textAlign="right">
              {t('COMPANY-SCAN_INPUT_INDEX-PERCENTAGE_TOTAL-EXCEEDS-100')}
            </Text>
          )}
        </VStack>
        <Flex justify="flex-end">
          <Tooltip
            label={
              enumOptions.length > 0
                ? t(
                    'COMPANY-SCAN_INPUT_INDEX-PERCENTAGE_ADD-BUTTON_DISABLED_ALL-ACTIVITIES-ADDED',
                  )
                : t(
                    'COMPANY-SCAN_INPUT_INDEX-PERCENTAGE_ADD-BUTTON_DISABLED_NO-ACTIVITIES',
                  )
            }
            isDisabled={availableOptions.length > 0 && enumOptions.length > 0}
            shouldWrapChildren
          >
            <Button
              leftIcon={<Icon name="GeneralActionsAdd" />}
              onClick={handleAddNewEntry}
              isDisabled={
                availableOptions.length === 0 || question.rules.is_disabled
              }
            >
              {t('ADD')}
            </Button>
          </Tooltip>
        </Flex>
      </VStack>
      <FormErrorMessage justifyContent="flex-end">
        {error?.message}
      </FormErrorMessage>
    </FormControl>
  );
};

export default IndexPercentageInputComponent;
